import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type ITableProps, Table } from "@similarweb/pro-logic-components";
import { Stack } from "@similarweb/ui-components/foundations";
import { useCurrentStateAndParams } from "@uirouter/react";

import { swSettings } from "app/common/services/swSettings";
import categoryService, { ALL } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { useAddToDashboard } from "app/components/dashboard/AddToDashboard/useAddToDashboard";
import type { ITableSort } from "app/components/React/Table/types";
import {
    getBonusCountryColumns,
    getColumns as defaultGetColumns,
} from "app/components/React/TopSites/DomainsTable/getColumns";
import { getNextSort } from "app/components/React/TopSites/DomainsTable/getNextSort";
import { getPaginationTrackingLabel } from "app/components/React/TopSites/DomainsTable/getPaginationTrackingLabel";
import { getSortTrackingEvent } from "app/components/React/TopSites/DomainsTable/getSortTrackingEvent";
import { LockedExcelButton } from "app/components/React/TopSites/DomainsTable/LockedExcelButton";
import { resolveExcelLink } from "app/components/React/TopSites/DomainsTable/resolveExcelLink";
import { useIndustryLeadersTable } from "app/components/React/TopSites/DomainsTable/useIndustryLeadersTable";
import { TopMoversStrip } from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/components/TopMoversStrip";
import type { ILeaderRow } from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/websiteRankings.types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { useNoRenderA2D } from "app/pages/industry-analysis/frame/WithNoRender";
import { getCategoryDetailsForDashboard } from "app/pages/industry-analysis/category-share/CategoryShareGraph/utils/CategoryShareGraphUtils";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { getPaidExcelProps } from "app/services/PaidExcelService/getPaidExcelProps";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { getSupportPeriod } from "app/UtilitiesAndConstants/UtilityFunctions/periodSupport";
import { WebsiteTypeFilter, type IWebsiteType } from "pro-features/components/filtersPanel/src/WebsiteTypeFilter";

const PAGE_SIZE = 100;
const FIRST_PAGE = 1;
const INITIAL_SORT: ITableSort = { field: "Share", sortDirection: "desc" };

export const IndustryLeadersTable = () => {
    const { params } = useCurrentStateAndParams();
    const { webSource, category, country, duration } = params;

    const [page, setPage] = useState(FIRST_PAGE);
    const [sort, setSort] = useState<ITableSort>(INITIAL_SORT);
    const [search, setSearch] = useState<string>();
    const [websiteType, setWebsiteType] = useState<IWebsiteType>();

    // A different industry, country, device or date range is a different dataset, and the
    // current page may not exist in it. Adjusted during render rather than in an effect, so
    // no throwaway request goes out for the old page first.
    const datasetKey = `${category}|${country}|${webSource}|${duration}`;
    const [datasetKeyOnLastReset, setDatasetKeyOnLastReset] = useState(datasetKey);
    if (datasetKey !== datasetKeyOnLastReset) {
        setDatasetKeyOnLastReset(datasetKey);
        setPage(FIRST_PAGE);
    }

    const filters = useMemo(
        () => ({ sort, search, funcFlag: websiteType ? Number(websiteType.id) : undefined }),
        [sort, search, websiteType],
    );

    const {
        rows,
        totalCount,
        topMovers,
        isTopMoversLoading,
        isLoading,
        isError,
        excelEndpoint,
        isBonusCountry,
        requestParams,
    } = useIndustryLeadersTable({ params, filters, page });

    const industry: ICategory = categoryService.categoryQueryParamToCategoryObject(category);
    const monthlySupport = swSettings.components.IndustryAnalysis.resources.Duration as number;
    const periodSupport = getSupportPeriod(monthlySupport);
    const hasSegments = CategoryServiceUtils.hasSegments(industry);
    const showIndustryColumn =
        !industry || industry.id === ALL || industry.isCustomCategory || Boolean(industry.children?.length);

    // The persisted layout is keyed by column key, but the column *set* varies: bonus
    // countries collapse to six columns, segments drop three, and webSource swaps Unique
    // users for Deduplicated audience. Scoping the storage key per variant stops a layout
    // saved under one from dropping or duplicating a column under another.
    const localStorageKey = `websiteRankingsTable-${isBonusCountry ? "bonus" : webSource}${
        hasSegments ? "-segments" : ""
    }`;

    const columns = useMemo(() => {
        const args = { sort, page, pageSize: PAGE_SIZE, hasSegments, showIndustryColumn, totalCount };
        return isBonusCountry ? getBonusCountryColumns(args) : defaultGetColumns({ ...args, periodSupport, webSource });
    }, [sort, page, isBonusCountry, showIndustryColumn, hasSegments, periodSupport, webSource, totalCount]);

    // Derived from our own state rather than antd's proposed order; see getNextSort for how
    // the cleared third state antd can emit is handled.
    const handleSort = useCallback<NonNullable<ITableProps<ILeaderRow>["onSort"]>>(
        ({ columnKey }) => {
            const nextSort = getNextSort(sort, columnKey);
            const title = columns.find((column) => (column.key ?? column.dataIndex) === nextSort.field)?.title;
            const { name, eventFullName } = getSortTrackingEvent(
                String(title ?? nextSort.field),
                nextSort.sortDirection,
            );
            // `dispatchTrackEvent` is the only overload that carries custom attributes
            // (`trackEvent` is deprecated and takes none).
            allTrackers.dispatchTrackEvent("Sort", "click", name, { event_full_name: eventFullName });
            setSort(nextSort);
            setPage(FIRST_PAGE);
        },
        [columns, sort],
    );

    const handlePageChange = (fromPage: number, _pageSize: number, nextPage: number) => {
        allTrackers.trackEvent("Pagination", "click", getPaginationTrackingLabel(fromPage, nextPage));
        setPage(nextPage);
    };

    const handleSearchChange = (nextSearch: string) => {
        setSearch(nextSearch);
        setPage(FIRST_PAGE);
    };

    const handleWebsiteTypeItemClick = (nextWebsiteType: IWebsiteType) => {
        setWebsiteType(nextWebsiteType);
        setPage(FIRST_PAGE);
    };

    const handleCloseWebsiteTypeItem = () => {
        setWebsiteType(undefined);
        setPage(FIRST_PAGE);
    };

    const handleResizeEnd = (columnName: string) => {
        allTrackers.trackEvent("Column resize", "click", columnName);
    };

    // The legacy wrapper fired this on every completed load, repeat counts included, so key
    // it on the load finishing rather than on the count changing. Its error path set the
    // count to "Error", which its own `Number(...) >= 0` guard then rejected — so a failed
    // load tracked nothing, and that is preserved here.
    const wasLoading = useRef(false);
    useEffect(() => {
        const justFinishedLoading = wasLoading.current && !isLoading;
        wasLoading.current = isLoading;
        if (justFinishedLoading && !isError) {
            allTrackers.dispatchTrackEvent("Table", "table-results", String(totalCount));
        }
    }, [isLoading, isError, totalCount]);

    // Matches the card header in the design: the industry date range and country, in that
    // order. Both come straight off the route params.
    const subtitleFilters = useSubtitleFilters("date", "country");

    const { isA2DAvailable } = useNoRenderA2D();
    const { openModal: openAddToDashboardModal, modalElement: addToDashboardModalElement } = useAddToDashboard({
        modelType: "fromIndustry",
        webSource,
        type: "TopSitesTable",
        metric: "TopSitesExtended",
        overrideAddToDashboardParams: {
            ...requestParams,
            key: [getCategoryDetailsForDashboard({ category }, { categoryService })],
            family: "Industry",
        },
        industryQueryParams: category,
    });

    const isExcelAllowed = swSettings.current.resources.IsExcelAllowed;
    const paidExcelProps = useMemo(() => (isPaidExcelAllowed() ? getPaidExcelProps() : null), []);
    // No config renders no package button; LockedExcelButton stands in for it below.
    const excelExportConfig = useMemo(() => {
        if (!isExcelAllowed) {
            return undefined;
        }
        return {
            ...(paidExcelProps ?? {}),
            withPaidExcel: !!paidExcelProps,
            excelDownloadUrl: resolveExcelLink(excelEndpoint, requestParams, sort),
            totalCount,
        };
    }, [isExcelAllowed, paidExcelProps, excelEndpoint, requestParams, sort, totalCount]);

    return (
        <Stack
            gap="24px"
            data-automation="website-rankings-table-container"
        >
            {!isBonusCountry && (
                <TopMoversStrip
                    movers={topMovers}
                    isLoading={isTopMoversLoading}
                    isError={isError}
                />
            )}
            <WidgetContainer
                withBorder
                title={translate("website.rankings.table.title", {}, "Website rankings")}
                subtitleFilters={subtitleFilters}
                automationId="website-rankings-table-widget"
            >
                <Table<ILeaderRow>
                    // `Domain` alone is not unique: an industry with segments can carry several
                    // segments over the same underlying domain, differing only by `Id`. Website
                    // rows may have an empty `Id` (see SegmentOrDomainCell's `!type || !SegmentId`
                    // guard), so those fall back to the domain.
                    rowKey={(row) => row.Id || row.Domain}
                    theme="white"
                    // antd anchors a sticky header to the window, but Industry Analysis scrolls
                    // inside `.sw-layout-scrollable-element` (IndustryAnalysisRoute.tsx), so the
                    // header detached and floated over the rows instead of pinning to the table.
                    // Disabled rather than re-anchored — see the MR discussion.
                    sticky={false}
                    columnConfig={columns}
                    data={rows}
                    isLoading={isLoading}
                    loaderRowCount={PAGE_SIZE}
                    localStorageKey={localStorageKey}
                    onSort={handleSort}
                    onResizeEnd={handleResizeEnd}
                    pagination={{
                        pageSize: PAGE_SIZE,
                        total: totalCount,
                        current: page,
                        onChange: handlePageChange,
                    }}
                    noData={{
                        type: isError ? "error" : "no-data",
                        // The header carries the sort controls; hiding it on an empty table
                        // leaves no way to change the sort that caused the empty state.
                        showHeaderWhenEmpty: true,
                        title: translate("industry.leaders.table.no.data.title"),
                        subtitle: translate("industry.leaders.table.no.data.subtitle"),
                    }}
                >
                    <Table.TableTop borderBottom>
                        <Table.TableTop.Filters
                            filters={[
                                {
                                    filter: "search",
                                    initialValue: search,
                                    onSearchChange: handleSearchChange,
                                    placeholder: translate("forms.search.placeholder"),
                                },
                            ]}
                            factoryFilters={
                                <>
                                    <WebsiteTypeFilter
                                        websiteType={websiteType?.text ?? ""}
                                        onWebsiteTypeItemClick={handleWebsiteTypeItemClick}
                                        onCloseWebsiteTypeItem={handleCloseWebsiteTypeItem}
                                    />
                                    {!isExcelAllowed && <LockedExcelButton />}
                                </>
                            }
                        />
                        <Table.TableTop.Settings
                            columnPickerType="basic"
                            locale={{
                                addToDashboardTooltip: translate("keyword.analysis.table.add.to.dashboard"),
                                columnPicker: {
                                    apply: translate("common.apply"),
                                    tooltip: translate("workspace.sales.column.picker"),
                                    getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                        translate("common.columnsPicker.selectedAmount", {
                                            selected: selectedValue,
                                            total: totalValue,
                                        }),
                                    mandatoryField: translate("common.columnsPicker.mandatoryField"),
                                },
                            }}
                            excelExportConfig={excelExportConfig}
                            showAddToDashboard={isA2DAvailable}
                            onAddToDashboard={openAddToDashboardModal}
                        />
                    </Table.TableTop>
                </Table>
            </WidgetContainer>
            {addToDashboardModalElement}
        </Stack>
    );
};
